import { defineStore } from 'pinia'
import { useRouter } from 'vue-router';

import AuthService, { NoBackOfficeAccessError } from '@/services/auth/authService.ts';
import localPermissions from "@/constants/localPermissions.ts";

import type {
  LoginRequest,
  OTPRequest,
  LogoutResponse,
  MeResponse,
} from '@/types/services/auth/auth.type.ts';
import type { AuthState, InitialState, InitialUser } from '@/types/stores/authStore.type.ts';

const userLocal: string | null = localStorage.getItem('user')

const initialState: InitialState = userLocal
  ? {authenticated: true}
  : {authenticated: false}

const initialUser: InitialUser = userLocal
  ? JSON.parse(userLocal)
  : {}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    auth: initialState,
    user: initialUser,
    isAdmin: false,
    router: useRouter()
  }),
  actions: {
    async login(userData: LoginRequest): Promise<void> {
      await AuthService.login(userData)
    },
    async loginOTP(userData: OTPRequest): Promise<void> {
      try {
        await AuthService.loginOTP(userData);
        await this.me();
        this.auth.authenticated = true;
        await this.router.push('/');
      } catch {
        this.cleanState();
      }
    },
    async me(): Promise<void> {
      try {
        this.user = await AuthService.me()
      } catch (error) {
        if (error instanceof NoBackOfficeAccessError) {
          // Never keep a non back-office account signed in to the back office.
          await this.logout()
        }
        throw error
      }

      const role = (this.user as MeResponse).role;
      this.isAdmin = role === 'admin' || role === 'admin_manager';
    },
    async logout(): Promise<void> {
      const logoutResponse: LogoutResponse = await AuthService.logout()
      if (logoutResponse.statusCode === 200) {
        this.cleanState()
        await this.router.push('/login')
      }
    },
    cleanState(): void {
      this.auth.authenticated = false
      this.isAdmin = false
      this.user = {}
      localStorage.removeItem('user')
    },
    hasPermission(permissionRequired: string[] = []): boolean {
      if (!permissionRequired) return true;
      const userPermissions: string[] = (this.user as MeResponse).permissions;
      if (!Array.isArray(userPermissions)) return false;
      return permissionRequired.some((permission): boolean => {
        if (permission in localPermissions) {
          const permissionObj = localPermissions[permission as keyof typeof localPermissions]
          return userPermissions.some((perm) => perm === permissionObj.name)
        }
        return false
      })
    },
  }
})

